import { useState, useEffect } from "react";
import InputField from "./InputField";
import Datepicker, { parseThaiDisplayString } from "./Datepicker";
import Button from "./Button";
import { THAI_MONTH_NAMES, ENGLISH_MONTH_NAMES } from "../utils/date";

function normalizeDateValue(value) {
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
        const normalizedDate = new Date(value);
        normalizedDate.setHours(0, 0, 0, 0);
        return normalizedDate;
    }

    if (typeof value === "string") {
        const isoDateMatch = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (isoDateMatch) {
            return new Date(
                Number(isoDateMatch[1]),
                Number(isoDateMatch[2]) - 1,
                Number(isoDateMatch[3]),
            );
        }

        const parsedDate = parseThaiDisplayString(value);
        if (parsedDate instanceof Date && !Number.isNaN(parsedDate.getTime())) {
            parsedDate.setHours(0, 0, 0, 0);
            return parsedDate;
        }
    }

    return null;
}


/**
 * 
 * AppointmentDatePicker
 * A date picker component for selecting date for filtering appointments.
 *
 * @param {string|Date} value
 *   The currently selected date.
 * @param {Function} onSubmit
 *   A callback function that is called when the user submits their date selection.
 * @param {string|Date} [minDate]
 *   The minimum selectable date.
 * @param {string|Date} [maxDate]
 *   The maximum selectable date.
 * @param {boolean} [allowYearSearch=false]
 *   Whether the year field supports searching.
 * 
 */

export default function DatepickerPopover({
    value,
    onSubmit,
    minDate,
    maxDate,
    initialDate,
    doctorId,
    patientId,
    clinicBranchId,
    allowYearSearch = false,
    calendarEra = "BE",
}) {
    const eraOffset = calendarEra === "AD" ? 0 : 543;

    const [selectedDate, setSelectedDate] = useState(() => {
        return normalizeDateValue(initialDate ?? value);
    });

    const [defaultViewDate, setDefaultViewDate] = useState(selectedDate || new Date());
    const [yearQuery, setYearQuery] = useState(String(defaultViewDate.getFullYear() + eraOffset));

    // selectedMonth/selectedYear track the calendar's displayed month for the disabled-date fetch.
    // They are initialized from the initial view date and updated only by: dropdowns, onClickNextMonthDate.
    const [selectedMonth, setSelectedMonth] = useState(selectedDate ? selectedDate.getMonth() + 1 : defaultViewDate.getMonth() + 1);
    const [selectedYear, setSelectedYear] = useState(selectedDate ? selectedDate.getFullYear() : defaultViewDate.getFullYear());

    useEffect(() => {
        if (initialDate === undefined) {
            return;
        }

        const nextSelectedDate = normalizeDateValue(initialDate);
        setSelectedDate(nextSelectedDate);
        if (nextSelectedDate) {
            setDefaultViewDate(nextSelectedDate);
            setSelectedMonth(nextSelectedDate.getMonth() + 1);
            setSelectedYear(nextSelectedDate.getFullYear());
        }
    }, [initialDate]);

    // Keep yearQuery display in sync whenever the view date changes (e.g. arrow navigation).
    useEffect(() => {
        setYearQuery(String(defaultViewDate.getFullYear() + eraOffset));
    }, [defaultViewDate, eraOffset]);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const defaultMinDate = new Date(today);
    defaultMinDate.setFullYear(defaultMinDate.getFullYear() - 150);

    const defaultMaxDate = new Date(today);
    defaultMaxDate.setFullYear(defaultMaxDate.getFullYear() + 150);

    const effectiveMinDate = minDate ? new Date(minDate) : defaultMinDate;
    const effectiveMaxDate = maxDate ? new Date(maxDate) : defaultMaxDate;

    // Only syncs the calendar's visual position. selectedMonth/selectedYear are kept separate
    // and updated by onClickNextMonthDate (matching AppointmentModal's pattern).
    const handleViewChange = (newViewDate) => {
        setDefaultViewDate(newViewDate);
    };

    function buildMonthYearSelection() {
        const displayYear = selectedDate ? selectedDate.getFullYear() : defaultViewDate.getFullYear();
        const displayMonth = selectedDate ? selectedDate.getMonth() : defaultViewDate.getMonth();

        const minYear = effectiveMinDate.getFullYear();
        const minMonth = effectiveMinDate.getMonth();
        const maxYear = effectiveMaxDate.getFullYear();
        const maxMonth = effectiveMaxDate.getMonth();

        const months = calendarEra === "AD" ? ENGLISH_MONTH_NAMES : THAI_MONTH_NAMES;
        const monthChoices = months.map((month, index) => ([index + 1, month]))
            .filter(([monthValue]) => {
                const currentMonthIndex = monthValue - 1;
                if (displayYear === minYear && currentMonthIndex < minMonth) {
                    return false;
                }
                if (displayYear === maxYear && currentMonthIndex > maxMonth) {
                    return false;
                }
                return true;
            });

        const yearChoices = [];
        for (let year = minYear; year <= maxYear; year++) {
            yearChoices.push([year + eraOffset, year + eraOffset]);
        }

        const applyYearSelection = (rawValue) => {
            const val = String(rawValue ?? "").replace(/\D/g, "");
            setYearQuery(val);

            const valInt = parseInt(val, 10);
            const isYearInChoices = yearChoices.some(([y]) => y === valInt);

            if (!isYearInChoices) {
                return;
            }

            const year = valInt - eraOffset;
            let month = defaultViewDate.getMonth();

            if (year === minYear && month < minMonth) {
                month = minMonth;
            }
            if (year === maxYear && month > maxMonth) {
                month = maxMonth;
            }

            setSelectedYear(year);
            // Reset day to 1 to avoid invalid date across months/years.
            setDefaultViewDate(new Date(year, month, 1));
            if (selectedDate) {
                setSelectedDate(new Date(year, month, 1));
            }
        };

        return (
            <div className="flex gap-3">
                <div className="flex-1">
                    <InputField
                        name="month"
                        type="select"
                        icon="calendar"
                        dropdownExtraClasses="dropdown-list-padding-2"
                        preventPopoverCloseOnClick
                        value={displayMonth + 1}
                        choices={monthChoices}
                        onChange={(e) => {
                            setSelectedMonth(parseInt(e.target.value, 10));
                            const month = parseInt(e.target.value, 10) - 1;
                            const year = selectedDate ? selectedDate.getFullYear() : defaultViewDate.getFullYear();

                            // Reset day to 1 to avoid invalid date across months/years.
                            setDefaultViewDate(new Date(year, month, 1));
                            if (selectedDate) {
                                setSelectedDate(new Date(year, month, 1));
                            }
                        }}
                    />
                </div>
                <div className="flex-1">
                    {allowYearSearch ? (
                        <InputField
                            name="year"
                            type="search"
                            icon="calendar"
                            dropdownExtraClasses="dropdown-list-padding-2"
                            preventPopoverCloseOnClick
                            value={yearQuery}
                            suggestions={yearChoices}
                            onSearch={async (query) => {
                                const val = query.replace(/\D/g, '');
                                if (!val) {
                                    return yearChoices;
                                }
                                return yearChoices.filter(([, display]) =>
                                    String(display).includes(val)
                                );
                            }}
                            onChange={(e) => {
                                const rawValue = e?.target?.selectedSuggestionValue ?? e?.target?.value;
                                applyYearSelection(rawValue);
                            }}
                            onBlur={() => {
                                const valInt = parseInt(yearQuery, 10);
                                const isYearInChoices = yearChoices.some(([y]) => y === valInt);
                                if (!isYearInChoices) {
                                    setYearQuery(String(defaultViewDate.getFullYear() + eraOffset));
                                }
                            }}
                        />
                    ) : (
                        <InputField
                            name="year"
                            type="select"
                            icon="calendar"
                            dropdownExtraClasses="dropdown-list-padding-2"
                            preventPopoverCloseOnClick
                            value={displayYear + eraOffset}
                            choices={yearChoices}
                            onChange={(e) => {
                                applyYearSelection(e?.target?.value);
                            }}
                        />
                    )}
                </div>
            </div>
        );
    }

    const normalizedInitialDate = normalizeDateValue(initialDate);
    const shouldDisableButton = !selectedDate || selectedDate?.getTime() === normalizedInitialDate?.getTime();

    return (
        <div className="flex flex-col w-[358px] rounded-xl p-5 gap-5 bg-main-background-system-default border border-main-border-system-default">
            {buildMonthYearSelection()}
            <div className="[&_.datepicker-picker]:p-0 [&_.datepicker-picker]:w-full [&_.datepicker-view]:px-0">
                <Datepicker
                    id="calendar"
                    inline
                    headless
                    defaultViewDate={defaultViewDate}
                    value={selectedDate}
                    minDate={effectiveMinDate}
                    maxDate={effectiveMaxDate}
                    doctorId={doctorId}
                    patientId={patientId}
                    clinicBranchId={clinicBranchId}
                    commonProps={{
                        onChange: (date) => {
                            setSelectedDate(date);
                            // Keep the calendar view on the selected date's month,
                            // matching AppointmentModal's setCalendarViewDate behaviour.
                            if (date instanceof Date && !isNaN(date)) {
                                setDefaultViewDate(date);
                            }
                        }
                    }}
                    onViewChange={handleViewChange}
                    selectedMonth={selectedMonth}
                    selectedYear={selectedYear}
                    onClickNextMonthDate={(newMonth) => setSelectedMonth(newMonth)}
                    calendarEra={calendarEra}
                />
            </div>
            <hr className="divider-horizontal" />
            <Button
                hierarchy="primary"
                size="md"
                text="ตกลง"
                onClick={() => {
                    onSubmit(selectedDate);
                }}
                className="w-full"
                disabled={shouldDisableButton}
            />
        </div>
    )
}
